import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ThresholdConfig {
  warning: number;
  critical: number;
  active: boolean;
}

// Stable config IDs — human-readable labels live in the UI layer only
export const THRESHOLD_KEYS = [
  "margin_drift_committed",
  "margin_drift_actual",
  "override_exposure_pct",
  "unattributed_cost_pct",
  "sov_line_margin_floor",
  "actual_vs_estimate_pct",
  "co_margin_dilution",
] as const;

export type ThresholdKey = (typeof THRESHOLD_KEYS)[number];

const DEFAULT_THRESHOLDS: Record<ThresholdKey, ThresholdConfig> = {
  margin_drift_committed: { warning: 3, critical: 8, active: true },
  margin_drift_actual: { warning: 3, critical: 8, active: true },
  override_exposure_pct: { warning: 5, critical: 15, active: true },
  unattributed_cost_pct: { warning: 3, critical: 10, active: true },
  sov_line_margin_floor: { warning: 15, critical: 5, active: true },
  actual_vs_estimate_pct: { warning: 110, critical: 130, active: true },
  co_margin_dilution: { warning: 3, critical: 8, active: true },
};

export const useAlertThresholds = () => {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["alert_thresholds"],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("alert_thresholds" as any) as any)
        .select("threshold_key, warning_value, critical_value, is_active");
      if (error) throw error;

      // Merge DB rows over defaults
      const merged = { ...DEFAULT_THRESHOLDS };
      (data ?? []).forEach((row: any) => {
        const key = row.threshold_key as ThresholdKey;
        if (key in merged) {
          merged[key] = {
            warning: Number(row.warning_value),
            critical: Number(row.critical_value),
            active: Boolean(row.is_active),
          };
        }
      });
      return merged;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // thresholds rarely change
  });

  const getThreshold = (key: ThresholdKey): ThresholdConfig => {
    return query.data?.[key] ?? DEFAULT_THRESHOLDS[key];
  };

  return {
    thresholds: query.data ?? DEFAULT_THRESHOLDS,
    getThreshold,
    isLoading: query.isLoading,
    defaults: DEFAULT_THRESHOLDS,
  };
};
